import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import {
    clearApiRequests,
    subscribeApiRequests,
} from "./apiRequestMonitor";
import styles from "./LocalhostApiPanel.module.scss";

const formatDuration = (durationMs) => {
    if (typeof durationMs !== "number") {
        return "…";
    }
    if (durationMs < 1000) {
        return `${durationMs} ms`;
    }
    return `${(durationMs / 1000).toFixed(2)} s`;
};

const formatTime = (timestamp) => {
    if (!timestamp) {
        return "";
    }
    return new Date(timestamp).toLocaleTimeString();
};

const getRequestTitle = (entry) => {
    try {
        const url = new URL(entry.fullUrl, window.location.origin);
        const pathParts = url.pathname.split("/").filter(Boolean);
        const endpoint = pathParts[pathParts.length - 1] || url.pathname || entry.fullUrl;
        return endpoint;
    } catch {
        return entry.fullUrl;
    }
};

const getRequestSubtitle = (entry) => {
    try {
        const url = new URL(entry.fullUrl, window.location.origin);
        const path = url.pathname;
        const query = url.search ? url.search : "";
        const combined = `${path}${query}`;
        return combined.length > 120 ? `${combined.slice(0, 120)}…` : combined;
    } catch {
        return entry.fullUrl.length > 120 ? `${entry.fullUrl.slice(0, 120)}…` : entry.fullUrl;
    }
};

const getStatusClassName = (entry) => {
    if (entry.status === "pending") {
        return styles.statusPending;
    }
    if (entry.status === "cancelled") {
        return styles.statusCancelled;
    }
    if (entry.ok === false || entry.status === "error") {
        return styles.statusError;
    }
    return styles.statusOk;
};

const getStatusLabel = (entry) => {
    if (entry.status === "pending") {
        return "en cours";
    }
    if (entry.status === "cancelled") {
        return "annulée";
    }
    if (entry.status === "error") {
        return "erreur";
    }
    return String(entry.status);
};

export default function LocalhostApiPanel() {
    const [isOpen, setIsOpen] = useState(false);
    const [requests, setRequests] = useState([]);
    const [selectedId, setSelectedId] = useState(null);

    useEffect(() => subscribeApiRequests(setRequests), []);

    useEffect(() => {
        if (!requests.length) {
            setSelectedId(null);
            return;
        }
        if (!requests.some((entry) => entry.id === selectedId)) {
            setSelectedId(requests[0].id);
        }
    }, [requests, selectedId]);

    const selectedEntry = useMemo(
        () => requests.find((entry) => entry.id === selectedId) || null,
        [requests, selectedId]
    );

    const handleCopy = async (value) => {
        if (!value || !navigator?.clipboard) {
            return;
        }
        await navigator.clipboard.writeText(value);
    };

    return (
        <>
            <button
                type="button"
                className={styles.toggle}
                onClick={() => setIsOpen((previous) => !previous)}
                aria-expanded={isOpen}
                aria-label="Ouvrir le panneau des requêtes API"
            >
                API
            </button>

            {isOpen ? (
                <aside className={styles.panel} aria-label="Panneau des requêtes API">
                    <div className={styles.header}>
                        <div>
                            <h2>Requêtes API</h2>
                            <p>{requests.length} requête(s) capturée(s)</p>
                        </div>
                        <div className={styles.headerActions}>
                            <button
                                type="button"
                                className={styles.actionButton}
                                onClick={() => clearApiRequests()}
                            >
                                Effacer
                            </button>
                            <button
                                type="button"
                                className={styles.actionButton}
                                onClick={() => setIsOpen(false)}
                            >
                                Fermer
                            </button>
                        </div>
                    </div>

                    <div className={styles.content}>
                        <section className={styles.listSection} aria-label="Liste des requêtes">
                            <div className={styles.listHeader}>Liste des requêtes</div>
                            <div className={styles.list}>
                                {requests.length === 0 ? (
                                    <p className={styles.empty}>Aucune requête pour le moment.</p>
                                ) : (
                                    requests.map((entry) => {
                                        const isSelected = selectedId === entry.id;
                                        const title = getRequestTitle(entry);

                                        return (
                                            <button
                                                key={entry.id}
                                                type="button"
                                                className={classNames(styles.listItem, isSelected && styles.listItemSelected)}
                                                onClick={() => setSelectedId(entry.id)}
                                            >
                                                <div className={styles.listItemTop}>
                                                    <span className={styles.method}>{entry.method}</span>
                                                    <span className={styles.listItemTitle} title={title}>
                                                        {title}
                                                    </span>
                                                </div>
                                                <div className={styles.listItemMeta}>
                                                    <span className={classNames(getStatusClassName(entry))}>
                                                        {getStatusLabel(entry)}
                                                    </span>
                                                    <span>{formatDuration(entry.durationMs)}</span>
                                                    <span>{formatTime(entry.startedAt)}</span>
                                                    <span>{entry.source}</span>
                                                </div>
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        </section>

                        <section className={styles.detailSection} aria-label="Détail de la requête">
                            <div className={styles.detailHeader}>
                                {selectedEntry ? getRequestTitle(selectedEntry) : "Détail"}
                            </div>
                            <div className={styles.detailBody}>
                                {!selectedEntry ? (
                                    <p className={styles.empty}>Sélectionnez une requête dans la liste.</p>
                                ) : (
                                    <>
                                        <div className={styles.detailMeta}>
                                            <span className={styles.method}>{selectedEntry.method}</span>
                                            <span className={classNames(getStatusClassName(selectedEntry))}>
                                                {getStatusLabel(selectedEntry)}
                                            </span>
                                            <span className={styles.duration}>
                                                {formatDuration(selectedEntry.durationMs)}
                                            </span>
                                            <span className={styles.duration}>
                                                {formatTime(selectedEntry.startedAt)}
                                            </span>
                                        </div>

                                        <div>
                                            <div className={styles.label}>Chemin</div>
                                            <div className={styles.pathLine}>{getRequestSubtitle(selectedEntry)}</div>
                                        </div>

                                        <div>
                                            <div className={styles.label}>Requête complète</div>
                                            <pre className={styles.codeBlock}>{selectedEntry.fullUrl}</pre>
                                        </div>

                                        {selectedEntry.requestBody ? (
                                            <div>
                                                <div className={styles.label}>Corps de la requête</div>
                                                <pre className={styles.codeBlock}>{selectedEntry.requestBody}</pre>
                                            </div>
                                        ) : null}

                                        {selectedEntry.errorMessage ? (
                                            <div>
                                                <div className={styles.label}>Erreur</div>
                                                <pre className={styles.codeBlock}>{selectedEntry.errorMessage}</pre>
                                            </div>
                                        ) : null}

                                        <button
                                            type="button"
                                            className={styles.actionButton}
                                            onClick={() => handleCopy(selectedEntry.fullUrl)}
                                        >
                                            Copier l&apos;URL
                                        </button>
                                    </>
                                )}
                            </div>
                        </section>
                    </div>
                </aside>
            ) : null}
        </>
    );
}
